import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api-client';
import { enumLabel } from '../lib/labels';
import { applyTableQuery, nextSort, type ColumnSort } from '../lib/list-query';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';
import { formatMoney, penceToPoundsInput, poundsToPence, vatPenceOnLines, vatPercentForCurrency, vatRateValue } from '../lib/money';
import { useLinkedPreview } from '../lib/linked-scroll';

function netOf(row: Record<string, unknown>): number {
  if (typeof row.netPence === 'number') return row.netPence;
  const lines = Array.isArray(row.lines) ? row.lines : [];
  const sum = lines.reduce((total, line) => total + Number((line as { amountPence?: number }).amountPence || 0), 0);
  return sum || Number(row.amountPence || 0);
}
import { apiError } from '../lib/utils';
import { useLedgerReload } from '../lib/use-ledger';
import { copyDocument, stashDuplicate, takeDuplicate } from '../lib/duplicate';
import { loadedRecordKey, useEditGuard } from '../lib/dirty';
import { bankLinesOf, chosenBank, type BankAccount } from '../lib/bank';
import { useAuth } from '../contexts/AuthContext';
import { useOurContact } from '../lib/our-contact';
import { useTermsSeed } from '../lib/terms-template';
import { renderEmailTemplate } from '../lib/email-template';
import { EmailTemplateSelect, SupplierTemplateSelect, TermsTemplateSelect } from '../components/documents/TemplateSelects';
import { useNotice } from '../components/ui/Notice';
import { EditWithAiButton, RecordAssistDialog } from '../components/records/RecordAssistDialog';
import { proposedDueDate } from '../lib/billing-entity';
import { EmailPreview } from '../components/documents/EmailPreview';
import { InvoiceFormat2 } from '../components/documents/InvoiceFormat2';
import { InvoicePreview, orderRef } from '../components/documents/InvoicePreview';
import { LineTable } from '../components/documents/LineTable';
import { VatRateSelect } from '../components/documents/VatRateSelect';
import { IssueHistory, MarkStatus, revisionLabel } from '../components/documents/IssueHistory';
import { Alert } from '../components/ui/Badge';
import { RecordStepper } from '../components/records/RecordStepper';
import { PageHead, RecordLink, SectionCard, SortHeader, StatusPill } from '../components/ui/chrome';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Field, GrowingText, Input, Select, TextArea } from '../components/ui/Input';
import { customerChoices, engagementChoices, purchaseOrderChoices, useFreshRows } from '../lib/select-choices';
import { creditNoteTabs } from '../lib/credit-note-tabs';
import { CreditNotePane, type CreditNoteRecord } from '../components/documents/CreditNotePane';

interface Line {
  description: string;
  qty: number;
  unitPounds: string;
  amountPence: number;
  source: string;
  poId: string;
  poLineId: string;
  chargeLineIds: string[];
}

interface Named { id: string; name: string; code?: string; description?: string; customerId?: string; customerCode?: string; customerName?: string; engagementId?: string; poNumber?: string; poId?: string; qty?: number; unitPence?: number; billingEmails?: string[]; contactName?: string; postalAddress?: string; legalName?: string; billingCurrency?: string; paymentTermsType?: string; paymentTermsDays?: number; paymentTermsDay?: number; paymentTermsConfirmed?: boolean; kind?: string; billToName?: string; billToAddress?: string; billToEmail?: string }

function chargeAmountPence(row: { qty?: number; unitPence?: number }): number {
  const unit = Number(row.unitPence);
  if (!Number.isInteger(unit) || unit <= 0) return 0;
  const qty = Number(row.qty);
  return Math.round((Number.isFinite(qty) && qty > 0 ? qty : 1) * unit);
}

function chargeIds(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function pricedFromCharges(poDescription: string, charges: { name: string; qty?: number; unitPence?: number }[]) {
  const amountPence = charges.reduce((sum, row) => sum + chargeAmountPence(row), 0);
  return {
    description: [poDescription.trim(), ...charges.map((row) => row.name.trim()).filter(Boolean)].filter(Boolean).join(' · '),
    qty: 1,
    unitPounds: penceToPoundsInput(amountPence),
    amountPence,
  };
}

interface PoLineOption {
  id: string;
  reference: string;
  description: string;
  amountPence: number;
  remainingPence: number;
}

interface PoOption extends Named {
  currency?: string;
  lines?: PoLineOption[];
}

export function InvoicePage() {
  const feedback = useNotice();
  const { can } = useAuth();
  const { id } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const markCleanRef = useRef<() => void>(() => {});
  const emailTouched = useRef(false);
  const [chargeLines, setChargeLines] = useState<Named[]>([]);
  const [loadedSpend, setLoadedSpend] = useState<Record<string, number>>({});
  const [inv, setInv] = useState<Record<string, unknown> | null>(null);
  const [events, setEvents] = useState<Record<string, unknown>[]>([]);
  const [eventSearch, setEventSearch] = useState('');
  const [eventSort, setEventSort] = useState<ColumnSort | null>(null);
  const eventColumns = useColumnSearch();
  const [customerId, setCustomerId] = useState('');
  const [engagementId, setEngagementId] = useState('');
  const [chargeLineId, setChargeLineId] = useState(search.get('chargeLineId') || '');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [issuedOn, setIssuedOn] = useState(new Date().toISOString().slice(0, 10));
  const [dueOn, setDueOn] = useState('');
  const [poId, setPoId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const numberTouched = useRef(false);
  const [currency, setCurrency] = useState('GBP');
  const [title, setTitle] = useState('');
  const [validDays, setValidDays] = useState(0);
  const [billToName, setBillToName] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [billToDetails, setBillToDetails] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  useOurContact(!id || id === 'new', (contact) => {
    setContactName((value) => value || contact.contactName);
    setContactRole((value) => value || contact.contactRole);
    setContactEmail((value) => value || contact.contactEmail);
    setContactPhone((value) => value || contact.contactPhone);
  });
  const [notice, setNotice] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [delivery, setDelivery] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [purpose, setPurpose] = useState('');
  const [scope, setScope] = useState('');
  useTermsSeed('invoice', (!id || id === 'new') && search.get('duplicate') !== '1', (row) => {
    setPaymentTerms((value) => value || row.paymentTerms);
    setDelivery((value) => value || row.delivery);
    setNotice((value) => value || row.notice);
    setAcceptance((value) => value || row.acceptance);
  });
  const [vat, setVat] = useState('20');
  const [layout, setLayout] = useState<'1' | '2'>('2');
  const [showPreview, setShowPreview] = useState(false);
  const { previewRef, onFormScroll } = useLinkedPreview();
  const [askCredit, setAskCredit] = useState(false);
  const [assist, setAssist] = useState<'create' | 'edit' | null>(null);
  const [creditRows, setCreditRows] = useState<{ lineIndex: number; description: string; leftPence: number; selected: boolean; pounds: string }[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const duplicateApplied = useRef(false);
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([]);
  const [templatePick, setTemplatePick] = useState('');
  const billSeeded = useRef(false);
  const [unsavedCopy, setUnsavedCopy] = useState(false);

  function fillCopy(copy: Record<string, unknown>) {
    setInv(null);
    setEngagementId(String(copy.engagementId || ''));
    setChargeLineId(String(copy.chargeLineId || ''));
    setPeriodStart(String(copy.periodStart || ''));
    setPeriodEnd(String(copy.periodEnd || ''));
    setIssuedOn(String(copy.issuedOn || copy.periodEnd || copy.periodStart || new Date().toISOString().slice(0, 10)));
    setDueOn(String(copy.dueOn || ''));
    setEmailSubject(String(copy.emailSubject || ''));
    setEmailBody(String(copy.emailBody || ''));
    emailTouched.current = Boolean(copy.emailSubject || copy.emailBody);
    setCurrency(String(copy.currency || 'GBP').toUpperCase());
    setTitle(String(copy.title || ''));
    setValidDays(Number(copy.validDays || 0));
    setBillToName(String(copy.billToName || ''));
    setBillToAddress(String(copy.billToAddress || ''));
    setBillToEmail(String(copy.billToEmail || ''));
    setBillToDetails(String(copy.billToDetails || ''));
    setBankAccountId(String(copy.bankAccountId || ''));
    billSeeded.current = Boolean(copy.billToName || copy.billToAddress || copy.billToEmail);
    setContactName(String(copy.contactName || ''));
    setContactRole(String(copy.contactRole || ''));
    setContactEmail(String(copy.contactEmail || ''));
    setContactPhone(String(copy.contactPhone || ''));
    setNotice(String(copy.notice || ''));
    setPaymentTerms(String(copy.paymentTerms || ''));
    setDelivery(String(copy.delivery || ''));
    setAcceptance(String(copy.acceptance || ''));
    setPurpose(String(copy.purpose || ''));
    setScope(String(copy.scope || ''));
    setVat(vatPercentForCurrency(String(copy.currency || 'GBP')));
    setLayout(copy.layout === '1' ? '1' : '2');
    setPoId(String(copy.poId || ''));
    setCustomerId(String(copy.customerId || ''));
    const nextLines = ((copy.lines as Record<string, unknown>[]) || []).map((line) => ({
      description: String(line.description || ''),
      qty: Number(line.qty || 0),
      unitPounds: penceToPoundsInput(line.unitPence),
      amountPence: Number(line.amountPence || 0),
      source: String(line.source || 'manual'),
      poId: String(line.poId || copy.poId || ''),
      poLineId: String(line.poLineId || ''),
      chargeLineIds: chargeIds(line.chargeLineIds),
    }));
    setLines(nextLines);
    setLoadedSpend({});
    setDocumentNumber('');
    numberTouched.current = false;
  }

  async function load(recordId?: string) {
    emailTouched.current = false;
    const target = recordId || id;
    if (!target || target === 'new') {
      if (duplicateApplied.current) return;
      const copy = search.get('duplicate') === '1' ? takeDuplicate('invoice') : null;
      if (copy) {
        duplicateApplied.current = true;
        setUnsavedCopy(true);
        fillCopy(copy);
        return;
      }
      setInv(null);
      setNotice('');
      setVat('20');
      setLayout('2');
      setLoadedSpend({});
      setDocumentNumber('');
      numberTouched.current = false;
      return;
    }
    const row = await apiGet<Record<string, unknown>>(`/api/invoices/${target}`);
    setInv(row || null);
    setEvents((row?.events as Record<string, unknown>[]) || []);
    if (!row) return;
    setEngagementId(String(row.engagementId || ''));
    setChargeLineId(String(row.chargeLineId || ''));
    setPeriodStart(String(row.periodStart || ''));
    setIssuedOn(String(row.issuedOn || row.periodEnd || row.periodStart || new Date().toISOString().slice(0, 10)));
    setDueOn(String(row.dueOn || ''));
    setEmailSubject(String(row.emailSubject || ''));
    setEmailBody(String(row.emailBody || ''));
    setDocumentNumber(String(row.internalNumber || row.number || ''));
    numberTouched.current = true;
    const invoiceCurrency = String(row.currency || 'GBP').toUpperCase();
    setCurrency(invoiceCurrency);
    setTitle(String(row.title || ''));
    setValidDays(Number(row.validDays || 0));
    setBillToName(String(row.billToName || ''));
    setBillToAddress(String(row.billToAddress || ''));
    setBillToEmail(String(row.billToEmail || ''));
    setBillToDetails(String(row.billToDetails || ''));
    setBankAccountId(String(row.bankAccountId || ''));
    setContactName(String(row.contactName || ''));
    setContactRole(String(row.contactRole || ''));
    setContactEmail(String(row.contactEmail || ''));
    setContactPhone(String(row.contactPhone || ''));
    setNotice(String(row.notice || ''));
    setPaymentTerms(String(row.paymentTerms || ''));
    setDelivery(String(row.delivery || ''));
    setAcceptance(String(row.acceptance || ''));
    setPurpose(String(row.purpose || ''));
    setScope(String(row.scope || ''));
    setVat(String(row.status || 'draft') === 'draft' ? vatPercentForCurrency(invoiceCurrency) : vatRateValue(netOf(row), typeof row.vatPence === 'number' ? row.vatPence : null));
    setLayout(row.layout === '1' ? '1' : '2');
    setPeriodEnd(String(row.periodEnd || ''));
    setPoId(String(row.poId || ''));
    setCustomerId(String(row.customerId || ''));
    const nextLines = ((row.lines as Record<string, unknown>[]) || []).map((line) => ({
      description: String(line.description || ''),
      qty: Number(line.qty || 0),
      unitPounds: penceToPoundsInput(line.unitPence),
      amountPence: Number(line.amountPence || 0),
      source: String(line.source || 'manual'),
      poId: String(line.poId || row.poId || ''),
      poLineId: String(line.poLineId || ''),
      chargeLineIds: chargeIds(line.chargeLineIds),
    }));
    setLines(nextLines);
    const spend: Record<string, number> = {};
    for (const line of nextLines) {
      if (!line.poLineId) continue;
      spend[line.poLineId] = (spend[line.poLineId] || 0) + line.amountPence;
    }
    setLoadedSpend(spend);
  }

  const [customers, reloadCustomers] = useFreshRows<Named>('/api/customers');
  const [engagements, reloadEngagements] = useFreshRows<Named>('/api/engagements');
  const [pos, reloadPos] = useFreshRows<PoOption>('/api/purchase-orders');

  useEffect(() => {
    if (!id || id === 'new') {
      void apiGet<{ id: string; name: string }[]>('/api/invoice-templates').then((list) => setTemplates(list || [])).catch(() => setTemplates([]));
    }
    void apiGet<Named[]>('/api/charge-lines').then(setChargeLines);
    void apiGet<BankAccount[]>('/api/bank-accounts').then((rows) => setBankAccounts(rows || [])).catch(() => setBankAccounts([]));
    void load().catch((err) => setError(apiError(err, 'Could not load')));
  }, [id]);

  useEffect(() => {
    if (customerId || !engagementId) return;
    const hit = engagements.find((row) => row.id === engagementId);
    if (hit?.customerId) setCustomerId(hit.customerId);
  }, [engagements, engagementId, customerId]);

  useEffect(() => {
    if (inv?.id || billSeeded.current) return;
    const line = chargeLines.find((row) => row.id === chargeLineId);
    const next = customers.find((row) => row.id === customerId);
    const name = line?.billToName || next?.legalName || next?.name || '';
    const address = line?.billToAddress || next?.postalAddress || '';
    const email = line?.billToEmail || next?.billingEmails?.[0] || '';
    if (!name && !address && !email) return;
    setBillToName(name);
    setBillToAddress(address);
    setBillToEmail(email);
  }, [inv?.id, chargeLineId, customerId, chargeLines, customers]);

  useEffect(() => {
    if (inv?.id || numberTouched.current || !customerId || !chargeLineId) return;
    const handle = window.setTimeout(() => {
      void apiGet<{ number: string }>(`/api/invoices/next-number?customerId=${encodeURIComponent(customerId)}&chargeLineId=${encodeURIComponent(chargeLineId)}`)
        .then((row) => {
          if (!numberTouched.current && row.number) setDocumentNumber(row.number);
        })
        .catch(() => undefined);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [customerId, chargeLineId, inv?.id]);

  useEffect(() => {
    if (inv?.id) return;
    if (emailSubject && emailBody) return;
    const number = String(inv?.internalNumber || inv?.number || documentNumber || 'Invoice');
    const customer = customers.find((row) => row.id === customerId)?.name || '';
    const po = pos.find((row) => row.id === (lines.find((line) => line.poId)?.poId || poId))?.poNumber || '';
    const poNumbers = [...new Set(lines.map((line) => pos.find((row) => row.id === line.poId)?.poNumber || '').filter(Boolean))];
    if (!poNumbers.length && po) poNumbers.push(po);
    if (!emailSubject) setEmailSubject(renderEmailTemplate('Invoice {{number}}', { number, customer, pos: poNumbers }));
    if (!emailBody) setEmailBody(renderEmailTemplate('Dear Accounts Payable,\n\nPlease find attached the invoice {{number}} against {{po}}\n\nMany thanks,', { number, customer, pos: poNumbers }));
  }, [inv, customers, pos, customerId, poId, lines, dueOn, emailSubject, emailBody]);

  useLedgerReload('invoices', () => { void load(); });

  const engagementsForCustomer = engagements.filter((row) => !customerId || row.customerId === customerId);
  const linesForEngagement = chargeLines.filter((row) => !engagementId || row.engagementId === engagementId);
  const posForCustomer = pos.filter((row) => !customerId || row.customerId === customerId);
  const customer = customers.find((row) => row.id === customerId);
  const previewLines = lines.map((line) => {
    const po = pos.find((row) => row.id === line.poId);
    const ref = po?.lines?.find((row) => row.id === line.poLineId)?.reference || '';
    return {
      qty: line.qty,
      description: line.description,
      unitPence: poundsToPence(line.unitPounds),
      amountPence: line.amountPence,
      orderRef: orderRef(po?.poNumber, ref),
    };
  });
  const total = useMemo(() => lines.reduce((sum, line) => sum + (line.amountPence || 0), 0), [lines]);
  const vatPence = vatPenceOnLines(lines.map((line) => line.amountPence || 0), vat);

  function leftOn(poLineId: string, index: number): number {
    const po = pos.find((row) => (row.lines || []).some((line) => line.id === poLineId));
    const orderLine = po?.lines?.find((line) => line.id === poLineId);
    if (!orderLine) return 0;
    const others = lines.reduce((sum, line, lineIndex) => sum + (lineIndex !== index && line.poLineId === poLineId ? line.amountPence : 0), 0);
    return orderLine.remainingPence + (loadedSpend[poLineId] || 0) - others;
  }

  function selectCustomer(nextId: string) {
    setCustomerId(nextId);
    setEngagementId('');
    setChargeLineId('');
    const next = customers.find((row) => row.id === nextId);
    setBillToName(next?.legalName || next?.name || '');
    setBillToAddress(next?.postalAddress || '');
    setBillToEmail(next?.billingEmails?.[0] || '');
    if (next?.billingCurrency) setCurrency(next.billingCurrency);
    if (issuedOn) setDueOn(proposedDueDate(issuedOn, next || {}));
  }

  function body() {
    return {
      engagementId,
      chargeLineId,
      periodStart,
      periodEnd,
      issuedOn,
      poId: '',
      dueOn: dueOn || undefined,
      emailSubject,
      emailBody,
      ...(inv?.id ? {} : { number: documentNumber, internalNumber: documentNumber }),
      title,
      validDays: validDays || undefined,
      billToName, billToAddress, billToEmail, billToDetails,
      bankAccountId,
      contactName, contactRole, contactEmail, contactPhone,
      notice, paymentTerms, delivery, acceptance, purpose, scope,
      layout,
      currency,
      vatPence,
      ...(vatPence == null ? {} : { netPence: total }),
      lines: lines.map((line) => ({
        description: line.description,
        qty: Number(line.qty || 0),
        unitPence: poundsToPence(line.unitPounds),
        amountPence: line.amountPence,
        source: line.source,
        poId: line.poId,
        poLineId: line.poLineId,
        chargeLineIds: line.chargeLineIds,
      })),
    };
  }

  async function propose() {
    const ok = await feedback.confirm('Propose lines', 'This will fill the invoice lines from the charge and the period. It does not save the invoice.');
    if (!ok) return;
    setError('');
    try {
      const proposed = await apiPost<{ poId?: string; lines?: Record<string, unknown>[] }>('/api/invoices/propose', { chargeLineId, periodStart, periodEnd });
      const line = chargeLines.find((row) => row.id === chargeLineId);
      if (line?.engagementId) setEngagementId(line.engagementId);
      if (proposed.poId) setPoId(proposed.poId);
      setLines((proposed.lines || []).map((item) => ({
        description: String(item.description || ''),
        qty: Number(item.qty || 0),
        unitPounds: penceToPoundsInput(item.unitPence),
        amountPence: Number(item.amountPence || 0),
        source: String(item.source || 'manual'),
        poId: String(item.poId || proposed.poId || ''),
        poLineId: String(item.poLineId || ''),
        chargeLineIds: chargeIds(item.chargeLineIds),
      })));
      feedback.success('Lines proposed');
    } catch (err) {
      feedback.failure(apiError(err, 'Propose failed'));
    }
  }

  async function applyTemplate(templateId: string) {
    setTemplatePick(templateId);
    if (!templateId) return;
    setError('');
    try {
      const copy = await apiGet<Record<string, unknown>>(`/api/invoice-templates/${templateId}`);
      fillCopy(copy);
      setUnsavedCopy(true);
      feedback.success('Template applied');
    } catch (err) {
      feedback.failure(apiError(err, 'Could not apply the template'));
    }
  }

  function stashInvoice() {
    if (!inv) return;
    stashDuplicate('invoice', copyDocument(inv, 'draft'));
  }

  async function saveAsTemplate() {
    if (!inv?.id) return;
    setError('');
    try {
      let invoiceId = String(inv.id);
      if (!locked) {
        const saved = await apiPut<Record<string, unknown>>(`/api/invoices/${invoiceId}`, body());
        setInv(saved);
        markCleanRef.current();
        invoiceId = String(saved.id || invoiceId);
      }
      const template = await apiPost<{ id: string }>('/api/invoice-templates/from-invoice', { invoiceId, name: title || documentNumber || undefined });
      navigate(`/invoice-templates/${template.id}`);
      feedback.success('Template saved');
    } catch (err) {
      feedback.failure(apiError(err, 'Could not save the template'));
    }
  }

  async function save() {
    if (inv?.status === 'issued' || inv?.status === 'paid' || inv?.status === 'credited') return;
    if (!chargeLineId) {
      feedback.failure('Choose a charge line.');
      return;
    }
    setError('');
    try {
      const existing = inv?.id ? String(inv.id) : '';
      const row = existing ? await apiPut<Record<string, unknown>>(`/api/invoices/${existing}`, body()) : await apiPost<Record<string, unknown>>('/api/invoices', body());
      setInv(row);
      markCleanRef.current();
      navigate(`/invoices/${row.id}`);
      feedback.success('Saved');
    } catch (err) {
      feedback.failure(apiError(err, 'Save failed'));
    }
  }

  markCleanRef.current = useEditGuard(loadedRecordKey(id, inv?.id ? String(inv.id) : undefined), JSON.stringify(body()), save);

  async function openReview() {
    if (!chargeLineId) {
      feedback.failure('Choose a charge line.');
      return;
    }
    setError('');
    try {
      const existing = inv?.id ? String(inv.id) : '';
      const row = existing ? await apiPut<Record<string, unknown>>(`/api/invoices/${existing}`, body()) : await apiPost<Record<string, unknown>>('/api/invoices', body());
      setInv(row);
      markCleanRef.current();
      navigate(`/invoices/${row.id}/review`);
      feedback.success('Saved');
    } catch (err) {
      feedback.failure(apiError(err, 'Save failed'));
    }
  }

  async function revise() {
    const ok = await feedback.confirm('Revise invoice', 'This will create a new draft revision of this issued invoice.');
    if (!ok) return;
    setError('');
    try {
      const saved = await apiPost<{ id: string }>(`/api/invoices/${inv?.id}/revise`, {});
      navigate(`/invoices/${saved.id}`);
      feedback.success('Revision created');
    } catch (err) {
      feedback.failure(apiError(err, 'Revise failed'));
    }
  }

  function openCredit() {
    const saved = (inv?.lines as { description?: string; amountPence?: number }[]) || [];
    const notes = (inv?.creditNotes as { lines?: { lineIndex: number; amountPence: number }[] }[]) || [];
    const used = new Map<number, number>();
    for (const note of notes) {
      for (const line of note.lines || []) used.set(line.lineIndex, (used.get(line.lineIndex) || 0) + Number(line.amountPence || 0));
    }
    const rows = saved.map((line, lineIndex) => {
      const left = Math.max(0, Number(line.amountPence || 0) - (used.get(lineIndex) || 0));
      return { lineIndex, description: String(line.description || ''), leftPence: left, selected: false, pounds: '' };
    }).filter((row) => row.leftPence > 0);
    setCreditRows(rows);
    setAskCredit(true);
  }

  async function createCredit(mode: 'draft' | 'review') {
    const chosen = creditRows.filter((row) => row.selected);
    if (!chosen.length) {
      feedback.failure('Select at least one invoice line.');
      return;
    }
    const lines = chosen.map((row) => ({ lineIndex: row.lineIndex, amountPence: poundsToPence(row.pounds) }));
    if (lines.some((line, index) => line.amountPence <= 0 || line.amountPence > chosen[index].leftPence)) {
      feedback.failure('Each selected line needs an amount up to what is left on that line.');
      return;
    }
    const row = await feedback.run(mode === 'draft' ? 'Creating the draft credit note' : 'Opening the credit note', () => apiPost<Record<string, unknown>>(`/api/invoices/${inv?.id}/credit-notes`, {
      lines,
    }), mode === 'draft' ? 'Draft credit note created' : 'Credit note ready', { confirm: false });
    const fresh = row?.id ? row : await apiGet<Record<string, unknown>>(`/api/invoices/${inv?.id}`).catch(() => null);
    if (fresh) setInv(fresh);
    if (row?.id) setAskCredit(false);
    const noteId = String(row?.createdCreditNoteId || '');
    if (mode === 'review' && noteId) openNote(noteId);
  }

  function openNote(noteId: string) {
    const params = new URLSearchParams(search);
    if (noteId) params.set('note', noteId);
    else params.delete('note');
    setSearch(params, { replace: true });
  }

  function detail(value: unknown) {
    if (!value || typeof value !== 'object') return '';
    return Object.entries(value as Record<string, unknown>).map(([key, item]) => `${key}=${Array.isArray(item) ? item.join(', ') : item}`).join(' · ');
  }

  const locked = inv?.status === 'issued' || inv?.status === 'paid' || inv?.status === 'credited';
  const creditNotes = Array.isArray(inv?.creditNotes) ? inv.creditNotes as CreditNoteRecord[] : [];
  const noteTabs = creditNoteTabs(creditNotes);
  const requestedNote = search.get('note') || '';
  const activeTab = noteTabs.some((item) => item.id === requestedNote) ? requestedNote : 'invoice';
  const activeNote = creditNotes.find((note) => note.id === activeTab) || null;

  return (
    <div className="space-y-4">
      {assist ? <RecordAssistDialog kind="invoice" mode={assist} recordId={inv?.id ? String(inv.id) : undefined} current={body()} title="Invoice" onClose={() => setAssist(null)} onSaved={(saved) => { const next = String(saved.id || ''); if (next && next !== id) navigate(`/invoices/${next}`); else void load(next); }} /> : null}
      {error ? <Alert error>{error}</Alert> : null}
      {inv?.lastError ? <Alert error>{String(inv.lastError)}</Alert> : null}
      {Array.isArray(inv?.billingWarnings) && inv.billingWarnings.length ? <Alert error>Billing profile needs attention: {(inv.billingWarnings as string[]).join('; ')}.</Alert> : null}
      {unsavedCopy ? <p className="text-sm text-[var(--bo-muted)]">Not saved yet. Save creates a new invoice.</p> : null}
      <PageHead
        stepper={id ? <RecordStepper listUrl="/api/invoices" id={id} basePath="/invoices" defaultFilter="drafts" /> : null}
        crumbs={[{ label: 'Invoices', to: '/invoices' }, { label: inv?.id ? String(inv.internalNumber || inv.number || 'Invoice') : 'New' }]}
        back={{ label: 'Back to invoices', to: '/invoices' }}
        title={inv?.id ? String(inv.internalNumber || inv.number || 'Invoice') : 'Invoice'}
        subtitle={inv?.id ? `${enumLabel(String(inv.status || ''))} · ${revisionLabel(inv.revision)}` : 'New invoice'}
        meta={inv?.creditTag === 'credited' || inv?.partCredited ? <StatusPill value={inv?.creditTag === 'credited' ? 'credited' : 'part_credited'} /> : null}
        actions={
        <div className="flex gap-2">
          {chargeLineId ? <Button variant="outline" onClick={() => void propose()}>Propose lines</Button> : null}
          {can('invoices', 'draft') && inv?.id ? <EditWithAiButton kind="invoice" status={inv.status} onClick={() => setAssist('edit')} /> : null}
          {can('invoices', 'draft') && !inv?.id ? <Button variant="outline" onClick={() => setAssist('create')}>Prepare with AI</Button> : null}
          {can('invoices', 'draft') && !locked ? <Button onClick={() => void save()}>Save</Button> : null}
          {inv?.id ? <Button variant="outline" onClick={() => { stashInvoice(); navigate('/invoices/new?duplicate=1'); }}>Duplicate</Button> : null}
          {inv?.id && can('invoices', 'draft') ? <Button variant="outline" onClick={() => void saveAsTemplate()}>Save as template</Button> : null}
          {inv?.status === 'draft' && can('invoices', 'issue') ? <Button onClick={() => void openReview()}>Review & issue</Button> : null}
          {inv?.status === 'issued' && can('invoices', 'issue') && (inv.deliveryStatus !== 'sent' || inv.filingStatus !== 'filed' || inv.lastError) ? <Button onClick={() => void openReview()}>{inv.deliveryStatus === 'sent' ? 'Retry filing' : 'Review & send'}</Button> : null}
          {inv?.status === 'issued' && can('invoices', 'draft') ? <Button variant="outline" onClick={() => void revise()}>Revise</Button> : null}
          {inv?.id && inv.status !== 'void' && inv.status !== 'draft' && can('invoices', 'draft') ? <Button variant="outline" onClick={openCredit}>Credit note</Button> : null}
          <MarkStatus id={inv?.id ? String(inv.id) : undefined} url="/api/invoices" status={inv?.status ? String(inv.status) : undefined} onDone={(row) => { setInv(row); setEvents((row.events as Record<string, unknown>[]) || []); }} />
          <Button variant="outline" onClick={() => setShowPreview((value) => !value)}>{showPreview ? 'Edit' : 'Preview'}</Button>
          {inv?.id ? <Button variant="outline" onClick={() => window.open(`/api/invoices/${inv.id}/pdf`, '_blank')}>PDF</Button> : null}
        </div>
      } />
      {noteTabs.length ? (
        <div className="flex gap-1 border-b border-[var(--bo-border)]">
          {noteTabs.map((item) => (
            <button key={item.id} type="button" className={`border-b-2 px-3 py-2 text-sm ${activeTab === item.id ? 'border-[#895CF6] text-[var(--bo-text)]' : 'border-transparent text-[var(--bo-muted)]'}`} onClick={() => openNote(item.id === 'invoice' ? '' : item.id)}>{item.label}</button>
          ))}
        </div>
      ) : null}
      <div className={activeTab === 'invoice' ? '' : 'hidden'}>
      <div className={`grid gap-6 ${showPreview ? 'h-[calc(100vh-16rem)] items-stretch xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]' : 'items-start'}`}>
      <div className={`min-w-0 space-y-4 ${showPreview ? 'min-h-0 overflow-y-auto pr-1' : ''}`} onScroll={showPreview ? onFormScroll : undefined}>
      <fieldset disabled={locked} className="m-0 min-w-0 space-y-4 border-0 p-0">
      <SectionCard title="Invoice" hint="The charge line sets the kind in the number. The number can be edited until the first save.">
      <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
        {!inv?.id ? <Field label="Template"><Select value={templatePick} onChange={(event) => void applyTemplate(event.target.value)}><option value="">None</option>{templates.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</Select></Field> : null}
        <Field label="Invoice number"><Input value={documentNumber} disabled={Boolean(inv?.id)} onChange={(event) => { numberTouched.current = true; setDocumentNumber(event.target.value); }} /></Field>
        <Field label="Format"><Select value={layout} onChange={(event) => setLayout(event.target.value === '1' ? '1' : '2')}><option value="2">Modern</option><option value="1">Traditional</option></Select></Field>
        <Field label="Customer"><Select searchable choices={customerChoices(customers)} onOpen={reloadCustomers} value={customerId} onChange={(event) => selectCustomer(event.target.value)} /></Field>
        <Field label="Engagement"><Select searchable choices={engagementChoices(engagementsForCustomer)} onOpen={reloadEngagements} value={engagementId} onChange={(event) => setEngagementId(event.target.value)} /></Field>
        <Field label="Charge line"><Select searchable value={chargeLineId} onChange={(event) => setChargeLineId(event.target.value)}><option value="">Select</option>{linesForEngagement.map((row) => <option key={row.id} value={row.id}>{row.kind ? `${row.name} · ${enumLabel(row.kind)}` : row.name}</option>)}</Select></Field>
        <Field label="Title"><Input value={title} onChange={(event) => setTitle(event.target.value)} /></Field>
        <Field label="Currency"><Select value={currency} onChange={(event) => { const next = event.target.value; setCurrency(next); setVat(vatPercentForCurrency(next)); }}><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="USD">USD</option></Select></Field>
        <Field label="Date"><Input type="date" value={issuedOn} onChange={(event) => { const nextDate = event.target.value; setIssuedOn(nextDate); if (!inv?.id && customer) setDueOn(proposedDueDate(nextDate, customer)); }} /></Field>
        <Field label="Period start"><Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></Field>
        <Field label="Period end"><Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></Field>
        <Field label="Expected pay date"><Input type="date" value={dueOn} onChange={(event) => setDueOn(event.target.value)} /><span className="mt-1 block text-xs text-[var(--bo-muted)]">Proposed from the billing entity’s payment terms{customer?.paymentTermsConfirmed ? '' : ' (terms not yet confirmed)'}.</span></Field>
        <Field label="Valid days"><Input type="number" value={validDays || ''} onChange={(event) => setValidDays(Number(event.target.value))} /></Field>
        <VatRateSelect value={vat} disabled onChange={setVat} />
        <Field label="Bank details"><Select value={chosenBank(bankAccounts, bankAccountId)?.id || ''} onChange={(event) => setBankAccountId(event.target.value)}><option value="">Select</option>{bankAccounts.map((row) => <option key={row.id} value={row.id}>{row.domestic ? `${row.name} · default` : row.name}</option>)}</Select></Field>
      </div>
      </SectionCard>
      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title="Client Details" hint="Who the invoice is addressed to. Details prints under Details on the right.">
          <div className="space-y-3">
            <Field label="Bill to"><Input value={billToName} onChange={(event) => { billSeeded.current = true; setBillToName(event.target.value); }} /></Field>
            <Field label="Address"><GrowingText value={billToAddress} onChange={(value) => { billSeeded.current = true; setBillToAddress(value); }} /></Field>
            <Field label="Email"><Input value={billToEmail} onChange={(event) => { billSeeded.current = true; setBillToEmail(event.target.value); }} /></Field>
            <Field label="Details"><GrowingText value={billToDetails} onChange={setBillToDetails} /></Field>
          </div>
        </SectionCard>
        <SectionCard title="Supplier Details" hint="The contact printed on the invoice.">
          <div className="space-y-3">
            <SupplierTemplateSelect onApply={(contact) => { setContactName(contact.contactName); setContactRole(contact.contactRole); setContactEmail(contact.contactEmail); setContactPhone(contact.contactPhone); }} />
            <Field label="Contact"><Input value={contactName} onChange={(event) => setContactName(event.target.value)} /></Field>
            <Field label="Role"><Input value={contactRole} onChange={(event) => setContactRole(event.target.value)} /></Field>
            <Field label="Email"><Input value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} /></Field>
            <Field label="Telephone"><Input value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} /></Field>
          </div>
        </SectionCard>
      </div>
      <SectionCard title="Email" hint="This text is used on Review and issue. {{number}} and {{po}} are filled when you pick a template.">
        <div className="space-y-3">
          <EmailTemplateSelect kind="invoice" values={{ number: documentNumber || String(inv?.internalNumber || inv?.number || ''), customer: customer?.name || '', title, pos: [...new Set(lines.map((line) => pos.find((row) => row.id === line.poId)?.poNumber || '').filter(Boolean))] }} onApply={(subject, body) => { emailTouched.current = true; setEmailSubject(subject); setEmailBody(body); }} />
          <Field label="Subject"><Input value={emailSubject} onChange={(event) => { emailTouched.current = true; setEmailSubject(event.target.value); }} /></Field>
          <Field label="Body"><GrowingText value={emailBody} onChange={(value) => { emailTouched.current = true; setEmailBody(value); }} /></Field>
        </div>
      </SectionCard>
      <LineTable
        columns={['Description', 'PO / line', 'Qty', 'Unit', 'Amount']}
        count={lines.length}
        locked={locked}
        addLabel="Add line item"
        onAdd={() => setLines((rows) => [...rows, { description: '', qty: 1, unitPounds: '', amountPence: 0, source: 'manual', poId: rows.at(-1)?.poId || poId, poLineId: '', chargeLineIds: [] }])}
        onRemove={(index) => setLines((rows) => rows.filter((_, i) => i !== index))}
        render={(index) => {
          const line = lines[index];
          const order = posForCustomer.find((row) => row.id === line.poId);
          const orderLines = order?.lines || [];
          const charges = linesForEngagement.filter((row) => !line.poId || !row.poId || row.poId === line.poId);
          return [
            <Input value={line.description} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, description: event.target.value } : item))} />,
            <div className="flex min-w-44 flex-col gap-1">
              <Select searchable className="w-full md:w-44" aria-label="Purchase order" choices={purchaseOrderChoices(posForCustomer)} onOpen={reloadPos} value={line.poId} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, poId: event.target.value, poLineId: '', chargeLineIds: [] } : item))} />
              <Select searchable className="w-full md:w-52" aria-label="Purchase order line" choices={[{ value: '', label: 'Line' }, ...orderLines.map((row) => ({ value: row.id, code: row.reference, description: `${row.description || ''}${row.description ? ' ' : ''}left ${formatMoney(leftOn(row.id, index), order?.currency || currency)}`.trim() }))]} onOpen={reloadPos} value={line.poLineId} onChange={(event) => setLines((rows) => rows.map((item, i) => {
                if (i !== index) return item;
                const orderLine = orderLines.find((row) => row.id === event.target.value);
                const selected = charges.filter((row) => item.chargeLineIds.includes(row.id));
                const priced = selected.length ? pricedFromCharges(orderLine?.description || '', selected) : { description: orderLine?.description || item.description };
                return { ...item, poLineId: event.target.value, ...priced };
              }))} />
              {charges.map((charge) => (
                <label key={charge.id} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    aria-label={`Charge ${charge.name}`}
                    checked={line.chargeLineIds.includes(charge.id)}
                    onChange={(event) => setLines((rows) => rows.map((item, i) => {
                      if (i !== index) return item;
                      const chargeLineIds = event.target.checked ? [...item.chargeLineIds, charge.id] : item.chargeLineIds.filter((value) => value !== charge.id);
                      const orderLine = orderLines.find((row) => row.id === item.poLineId);
                      const selected = charges.filter((row) => chargeLineIds.includes(row.id));
                      if (!selected.length) return { ...item, chargeLineIds, description: orderLine?.description || item.description };
                      return { ...item, chargeLineIds, ...pricedFromCharges(orderLine?.description || '', selected) };
                    }))}
                  />
                  {charge.name}
                </label>
              ))}
              {line.poId ? <RecordLink to={`/purchase-orders/${line.poId}`} backLabel={`Back to ${documentNumber || 'invoice'}`}>Open order</RecordLink> : null}
            </div>,
            <Input className="w-full md:w-24" type="number" step="0.25" value={line.qty} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, qty: Number(event.target.value), amountPence: Math.round(Number(event.target.value || 0) * poundsToPence(item.unitPounds)) } : item))} />,
            <Input className="w-full md:w-28" type="number" step="0.01" value={line.unitPounds} onChange={(event) => setLines((rows) => rows.map((item, i) => i === index ? { ...item, unitPounds: event.target.value, amountPence: Math.round(Number(item.qty || 0) * poundsToPence(event.target.value)) } : item))} />,
            <span className="tabular-nums">{formatMoney(line.amountPence, order?.currency || currency)}</span>,
          ];
        }}
      />
      <SectionCard title="Commercial terms" hint="Agreed terms for this invoice.">
      <TermsTemplateSelect kind="invoice" onApply={(row) => { setPaymentTerms(row.paymentTerms); setDelivery(row.delivery); setNotice(row.notice); setAcceptance(row.acceptance); }} />
      <div className="grid gap-3 lg:grid-cols-2">
        <Field label="Payment terms"><TextArea value={paymentTerms} onChange={(event) => setPaymentTerms(event.target.value)} /></Field>
        <Field label="Delivery"><TextArea value={delivery} onChange={(event) => setDelivery(event.target.value)} /></Field>
        <Field label="Notice / Terms"><TextArea value={notice} placeholder="[Terms and Conditions](https://maxiron.com/terms-and-conditions/)" onChange={(event) => setNotice(event.target.value)} /></Field>
        <Field label="To proceed"><TextArea value={acceptance} onChange={(event) => setAcceptance(event.target.value)} /></Field>
        <Field label="Purpose"><TextArea value={purpose} onChange={(event) => setPurpose(event.target.value)} /></Field>
        <Field label="Scope"><TextArea value={scope} onChange={(event) => setScope(event.target.value)} /></Field>
      </div>
      </SectionCard>
      <div className="mt-3 space-y-1 text-right">
        <div className="text-sm text-gray-500">Net {formatMoney(total, currency)} · VAT {vatPence == null ? 'NONE' : formatMoney(vatPence, currency)}</div>
        {inv?.gbpNetPence != null ? <div className="text-sm text-gray-500">GBP net {formatMoney(inv.gbpNetPence, 'GBP')} · GBP total {formatMoney(inv.gbpAmountPence, 'GBP')}{currency !== 'GBP' ? ` at ${String(inv.fxRate || '').replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')} pounds per 1 ${currency} (${String(inv.fxRateOn || '')})` : ''}</div> : null}
        {Number(inv?.creditedNetPence || 0) > 0 ? <div className="text-sm text-gray-500">Credited {formatMoney(Number(inv?.creditedNetPence || 0), currency)} · VAT {formatMoney(Number(inv?.creditedVatPence || 0), currency)} · Balance {formatMoney((vatPence == null ? total : total + vatPence) - Number(inv?.creditedNetPence || 0) - Number(inv?.creditedVatPence || 0), currency)}</div> : null}
        <div className="text-2xl">{formatMoney(vatPence == null ? total : total + vatPence, currency)}</div>
      </div>
      </fieldset>
      {inv?.id && lines.some((line) => line.poId) ? (
        <SectionCard title="Customer orders" hint="Orders used by the lines on this invoice.">
          <div className="flex flex-col gap-2">
            {[...new Map(lines.filter((line) => line.poId).map((line) => [line.poId, pos.find((row) => row.id === line.poId)?.poNumber || 'Order'])).entries()].map(([orderId, number]) => (
              <RecordLink key={orderId} to={`/purchase-orders/${orderId}`} backLabel={`Back to ${documentNumber || 'invoice'}`}>{number}</RecordLink>
            ))}
          </div>
        </SectionCard>
      ) : null}
      {inv?.id ? (
        <SectionCard title="Payments in" hint="Payments recorded against this invoice.">
          <div className="flex flex-col gap-2">
            <RecordLink to={`/payments-in?invoiceId=${encodeURIComponent(String(inv.id))}&invoiceNumber=${encodeURIComponent(documentNumber || String(inv.internalNumber || inv.number || ''))}`} backLabel={`Back to ${documentNumber || 'invoice'}`}>Open payments in</RecordLink>
            {((inv.paymentsIn as { id?: string; amountPence?: number; paidOn?: string }[]) || []).map((pay) => (
              <span key={String(pay.id)} className="text-sm">{formatMoney(Number(pay.amountPence || 0), currency)} · {String(pay.paidOn || '')}</span>
            ))}
          </div>
        </SectionCard>
      ) : null}
      {inv?.id ? <p className="mt-3 text-sm text-gray-500">{String(inv.internalNumber || inv.number || 'Invoice')} · {enumLabel(String(inv.status || ''))}{inv.booksKind === 'purchase_refund' ? ' · Purchase Refund' : ''} · {revisionLabel(inv.revision)} · {String(inv.customerName || '')} · PO {String(inv.poNumber || 'none')}</p> : null}
      {inv?.id ? <IssueHistory issues={inv.issues} sharePointUrl={typeof inv.sharePointUrl === 'string' ? inv.sharePointUrl : ''} /> : null}
      {events.length ? (
        <>
          <h3 className="mt-6 text-lg font-semibold">Event log</h3>
          <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <Input className="mb-3 h-8 max-w-xs" aria-label="Search event log" placeholder="Search event log" value={eventSearch} onChange={(event) => setEventSearch(event.target.value)} />
          <table className="bo-cards min-w-full text-sm">
            <thead><tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700">
              <SortHeader className="px-3 py-2 font-medium" label="When" active={eventSort?.key === 'at'} dir={eventSort?.dir || 'asc'} onSort={() => setEventSort((current) => nextSort(current, 'at'))} />
              <SortHeader className="py-2 font-medium" label="Who" active={eventSort?.key === 'actor'} dir={eventSort?.dir || 'asc'} onSort={() => setEventSort((current) => nextSort(current, 'actor'))} />
              <SortHeader className="py-2 font-medium" label="Action" active={eventSort?.key === 'action'} dir={eventSort?.dir || 'asc'} onSort={() => setEventSort((current) => nextSort(current, 'action'))} />
              <SortHeader className="py-2 font-medium" label="Detail" active={eventSort?.key === 'detail'} dir={eventSort?.dir || 'asc'} onSort={() => setEventSort((current) => nextSort(current, 'detail'))} />
              <th className="w-8 pr-2 text-right"><ColumnFilterToggle open={eventColumns.open} onClick={eventColumns.toggle} /></th>
            </tr>
            {eventColumns.open ? <ColumnFilterInputs columns={[{ key: 'at', label: 'When' }, { key: 'actor', label: 'Who' }, { key: 'action', label: 'Action' }, { key: 'detail', label: 'Detail' }]} draft={eventColumns.draft} onChange={eventColumns.set} trailing /> : null}
            </thead>
            <tbody>
              {applyTableQuery(events, eventSearch, [
                { key: 'at', text: (event) => String(event.at || ''), sort: (event) => String(event.at || '') },
                { key: 'actor', text: (event) => String(event.actor || '') },
                { key: 'action', text: (event) => String(event.action || '') },
                { key: 'detail', text: (event) => detail(event.detail) },
              ], eventSort).filter((event) => eventColumns.matches({
                at: String(event.at || ''),
                actor: String(event.actor || ''),
                action: String(event.action || ''),
                detail: detail(event.detail),
              })).map((event) => (
                <tr key={String(event.id)}>
                  <td data-label="When">{String(event.at || '')}</td>
                  <td data-label="Who">{String(event.actor || '')}</td>
                  <td data-label="Action">{String(event.action || '')}</td>
                  <td data-label="Detail">{detail(event.detail)}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      ) : null}
      </div>
      {showPreview ? <div ref={previewRef} className="min-h-0 min-w-0 space-y-4 overflow-y-auto">
        <div className="rounded-lg bg-[var(--bo-preview)] p-3">
          {layout === '1' ? (
          <InvoicePreview
            number={documentNumber || undefined}
            customerName={customer?.name || String(inv?.customerName || '')}
            billingEmails={customer?.billingEmails || []}
            issuedOn={issuedOn}
            dueOn={dueOn}
            currency={currency}
            poNumber=""
            billToName={billToName}
            billToAddress={billToAddress}
            billToEmail={billToEmail}
            billToDetails={billToDetails}
            bankLines={bankLinesOf(chosenBank(bankAccounts, bankAccountId))}
            title={title}
            contactName={contactName}
            contactRole={contactRole}
            contactEmail={contactEmail}
            contactPhone={contactPhone}
            validDays={validDays}
            paymentTerms={paymentTerms}
            delivery={delivery}
            acceptance={acceptance}
            purpose={purpose}
            scope={scope}
            notice={notice}
            vatPence={vatPence}
            lines={previewLines}
          />
          ) : (
          <InvoiceFormat2
            number={documentNumber || undefined}
            customerName={customer?.name || String(inv?.customerName || '')}
            billingEmails={customer?.billingEmails || []}
            issuedOn={issuedOn}
            dueOn={dueOn}
            currency={currency}
            poNumber=""
            billToName={billToName}
            billToAddress={billToAddress}
            billToEmail={billToEmail}
            billToDetails={billToDetails}
            bankLines={bankLinesOf(chosenBank(bankAccounts, bankAccountId))}
            title={title}
            contactName={contactName}
            contactRole={contactRole}
            contactEmail={contactEmail}
            contactPhone={contactPhone}
            validDays={validDays}
            paymentTerms={paymentTerms}
            delivery={delivery}
            acceptance={acceptance}
            purpose={purpose}
            scope={scope}
            notice={notice}
            vatPence={vatPence}
            lines={previewLines}
          />
          )}
        </div>
        <EmailPreview recipients={[billToEmail, ...(customer?.billingEmails || [])].map((email) => email.trim()).filter((email, index, all) => email && all.indexOf(email) === index).join(', ')} subject={emailSubject} body={emailBody} senderName={contactName} senderTitle={contactRole} senderEmail={contactEmail} senderPhone={contactPhone} />
      </div> : null}
      </div>
      </div>
      {activeNote && inv?.id ? (
        <CreditNotePane
          invoice={{
            id: String(inv.id),
            number: String(inv.number || ''),
            internalNumber: String(inv.internalNumber || ''),
            customerId: String(inv.customerId || ''),
            customerName: String(inv.customerName || ''),
            layout,
            currency,
            billToName,
            billToAddress,
            billToEmail,
            billToDetails,
            bankLines: bankLinesOf(chosenBank(bankAccounts, bankAccountId)),
            contactName,
            contactRole,
            contactEmail,
            contactPhone,
          }}
          note={activeNote}
          onIssued={() => void load(String(inv.id))}
        />
      ) : null}
      {askCredit ? (
        <Dialog title="Credit note" large onClose={() => setAskCredit(false)} footer={
          <>
            <Button variant="outline" onClick={() => setAskCredit(false)}>Cancel</Button>
            <Button variant="outline" onClick={() => void createCredit('draft')} disabled={!creditRows.some((row) => row.selected)}>Save draft</Button>
            <Button onClick={() => void createCredit('review')} disabled={!creditRows.some((row) => row.selected)}>Review & send</Button>
          </>
        }>
          <p className="text-sm text-[var(--bo-muted)]">One credit note is for this invoice. Select the lines to credit. The amount starts as what is left on that line and can be less. Review & send opens the credit note tab so you can check the PDF, the message, and who it goes to. It is not sent to Crunch.</p>
          {creditRows.length ? (
            <div className="space-y-2">
              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  aria-label="Select all lines"
                  checked={creditRows.every((row) => row.selected)}
                  onChange={(event) => {
                    const on = event.target.checked;
                    setCreditRows((rows) => rows.map((item) => ({ ...item, selected: on, pounds: on ? penceToPoundsInput(item.leftPence) : item.pounds })));
                  }}
                />
                Select all
              </label>
              {creditRows.map((row) => (
                <div key={row.lineIndex} className="grid grid-cols-[24px_minmax(0,1fr)_160px] items-center gap-3">
                  <input type="checkbox" aria-label={`Credit ${row.description || 'line'}`} checked={row.selected} onChange={(event) => setCreditRows((rows) => rows.map((item) => item.lineIndex === row.lineIndex ? { ...item, selected: event.target.checked, pounds: event.target.checked ? penceToPoundsInput(item.leftPence) : item.pounds } : item))} />
                  <div>
                    <div className="text-sm">{row.description}</div>
                    <div className="text-xs text-[var(--bo-muted)]">Left {formatMoney(row.leftPence, currency)}</div>
                  </div>
                  <Input aria-label={`Amount for ${row.description || 'line'}`} type="number" step="0.01" disabled={!row.selected} value={row.selected ? row.pounds : ''} onChange={(event) => setCreditRows((rows) => rows.map((item) => item.lineIndex === row.lineIndex ? { ...item, pounds: event.target.value } : item))} />
                </div>
              ))}
            </div>
          ) : <p className="text-sm">Every line on this invoice is already credited. Open the credit note tab to send it or download the PDF.</p>}
        </Dialog>
      ) : null}
    </div>
  );
}
